import React, { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

type Props = {
  imageUrl: string;
  onClose: () => void;
};

const CustomImageViewer: React.FC<Props> = ({ imageUrl, onClose }) => {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const direction = e.deltaY > 0 ? -0.1 : 0.1;
    setScale((prev) => Math.min(Math.max(prev + direction, 0.5), 5));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    setStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    setTranslate((prev) => ({
      x: prev.x + dx,
      y: prev.y + dy,
    }));
    setStart({ x: e.clientX, y: e.clientY });
  };

  const resetView = () => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  };

  return (
    <div
      ref={containerRef}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{
        position: "fixed",
        zIndex: 4000,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.9)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: dragging ? "grabbing" : "default",
      }}
    >
      <img
        src={imageUrl}
        alt="Preview"
        style={{
          transform: `scale(${scale}) translate(${translate.x / scale}px, ${
            translate.y / scale
          }px)`,
          transition: dragging ? "none" : "transform 0.2s ease",
          maxWidth: "90%",
          maxHeight: "90%",
          userSelect: "none",
          pointerEvents: "none",
        }}
        draggable={false}
      />

      <div
        style={{
          position: "fixed",
          top: 20,
          right: 20,
          display: "flex",
          gap: 10,
        }}
      >
        <button
          className="`ml-8 btn btn-primary"
          onClick={() => setScale((s) => Math.min(s + 0.2, 5))}
          style={buttonStyle}
        >
          <ZoomInIcon />
        </button>
        <button
          className="`ml-8 btn btn-primary"
          onClick={() => setScale((s) => Math.max(s - 0.2, 0.5))}
          style={buttonStyle}
        >
          <ZoomOutIcon />
        </button>
        <button
          className="`ml-8 btn btn-primary"
          onClick={resetView}
          style={buttonStyle}
        >
          <RestartAltIcon />
        </button>
        <button
          className="`ml-8 btn btn-primary"
          onClick={onClose}
          style={buttonStyle}
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
};

const buttonStyle: React.CSSProperties = {
  backgroundColor: "rgba(20, 10, 154)",
  border: "none",
  color: "white",
  fontSize: 14,
  cursor: "pointer",
  padding: 8,
  borderRadius: 4,
  fontFamily: "Noto Sans",
};

export default CustomImageViewer;
